import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";

/**
 * The language the mail to an owner is written in.
 *
 * The first selected owner decides it. With several owners the mail goes out
 * once, so one language has to win, and the broker picked that owner first.
 * An owner whose language we do not know reads Dutch, which is what the rest
 * of this flow falls back to (DEV-3339).
 */
export const DEFAULT_OWNER_LOCALE = ELocale.NL;

export const resolveOwnerLocale = (
  localeByAccountId: { [accountId: string]: ELocale | undefined },
  selectedAccountIds: number[],
): ELocale => {
  const first = selectedAccountIds[0];

  if (first === undefined) {
    return DEFAULT_OWNER_LOCALE;
  }

  return localeByAccountId[`${first}`] || DEFAULT_OWNER_LOCALE;
};

/**
 * What the broker typed, without the markup.
 *
 * The limit is on what an owner reads, not on what the editor stores: a
 * sentence in bold is the same sentence, and counting its tags would stop a
 * broker at a number they cannot see. The server holds the serialized HTML
 * with room to spare, so the markup costs them nothing (DEV-3339).
 */
export const getVisibleTextLength = (html?: string) => {
  const text = `${html || ""}`
    // A block boundary is a line break to a reader, so it counts as one
    // character rather than vanishing and gluing two words together.
    .replace(/<\/(p|div|h1|h2|li|blockquote)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

  return text.trim().length;
};

/** Whether this intro is short enough to send. */
export const isIntroWithinLimit = (html: string | undefined, max: number) =>
  getVisibleTextLength(html) <= max;

/**
 * Whether the broker wrote anything at all.
 *
 * An editor that has been opened and emptied still holds a paragraph or two,
 * so an empty intro is a question about the text rather than about the
 * string.
 */
export const hasIntroText = (html?: string) => getVisibleTextLength(html) > 0;
